/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2020-2021, Bulb Inc.  For more information, please email contact@hellobulb.com.
 *                _
 *  _            | | _
 * | |         _ | || |
 * | |__   _  | || || |__
 * | '_ \ | | | || || '_ \
 * | (_) || |_| || || (_) |
 *  \___/  \___/ |_| \___/
 */

import { Inject, Injectable } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';

import { AeroFormComponent } from '../../aero/aero-form/aero-form.component';

/**
 * Angular Service that handles alerting the user if changes have been made in a section, and try to navigate away.
 */
@Injectable()
export class SectionDataAlertService {
  /**
   * Configuration object for alert modal.
   *
   * @private
   */
  private loseInfoModalOptions: any = {
    fontAwesomeCode: 'fas fa-exclamation-triangle',
    titleCode: 'resume_lose_info_title',
    bodyCode: 'resume_lose_info_body',
    cancelButtonCode: 'button_continue',
    showCancelButton: false,
    windowClass: 'updated-modal-design bulb-aero-modal edit-lose-info-modal',
  };

  /**
   * Resume section overlay that we prepend to the current resume section.
   *
   * @private
   */
  private resumeOverlayDOMClassName: string = '.resume-section-overlay';

  /**
   * DOM class name for when the overlay is enabled for the resume section.
   *
   * @private
   */
  private resumeOverlayEnabledClassName: string = 'resume-section-overlay-enabled';

  /**
   * Reference to the current resume section's aero form component.
   * @private
   */
  private aeroFormComponent: any;

  /**
   * booleans indicating if the user has added or removed portfolio content to each resume section.
   * @private
   */
  private isPortfolioContentModified: boolean;

  /**
   * Sub section cancel function.
   * @private
   */
  private cancelFunction: any;

  /**
   * hold the reference of the overlay
   * @private
   */
  private overlay: any;

  /**
   * Default constructor.
   *
   * @param bulbModalService
   */
  constructor(@Inject('BulbModalService') private bulbModalService) {}

  /**
   * Helper method to enable overlay and lose content alert, if sub the section is open in edit mode.
   *
   * @param aeroFormComponent - form with in the target element.
   * @param onCancel - function which is called when user cancel the editing
   *
   */
  enableAlert(aeroFormComponent: any, onCancel: any): void {
    this.overlay = document.querySelector(this.resumeOverlayDOMClassName);
    this.overlay.style.display = 'block';
    document.querySelector(this.resumeOverlayDOMClassName).addEventListener('click', this.onOverlayClick);
    this.aeroFormComponent = aeroFormComponent;
    this.cancelFunction = onCancel;
    this.toggleEnabledClassOnTargetSection(this.resumeOverlayEnabledClassName, 'add');
  }

  /**
   * Helper method to remove the overlay when user finish editing either by cancelling or clicking save.
   */
  disableAlert(): void {
    document.querySelector(this.resumeOverlayDOMClassName).removeEventListener('click', this.onOverlayClick);
    this.overlay.style.display = 'none';
    this.toggleEnabledClassOnTargetSection(this.resumeOverlayEnabledClassName, 'remove');
    this.resetServiceValues();
  }

  /**
   * Helper method to add class enabled class to editing sub section.
   *
   * @param classToAdd - enabled class name.
   * @param action - add or remove.
   * @private
   */
  private toggleEnabledClassOnTargetSection(classToToggle: string, action: string): void {
    if (this.aeroFormComponent) {
      const sectionClassList = this.aeroFormComponent.elementReference.nativeElement.parentElement.classList;
      sectionClassList[action](classToToggle);
    }
  }

  /**
   * Callback method which is called when the user clicked on the overlay, outside of editable subsection area.
   */
  onOverlayClick = () => {
    if (this.isPortfolioContentModified || (this.sectionIsDirty(this.aeroFormComponent))) {
      SectionDataAlertService.registerBeforeUnloadEvent();
      this.displayAlertModal();
    } else {
      this.disableAlert();
      this.cancelFunction();
    }
  };

  /**
   * Helper method to reset the service values from context switching between sections.
   *
   * @private
   */
  private resetServiceValues(): void {
    this.aeroFormComponent = null;
    this.isPortfolioContentModified = false;
  }

  /**
   * Helper method to display the alert modal indicating to the user that they have unsaved changes.
   *
   * @private
   */
  private displayAlertModal(): void {
    this.bulbModalService.show(this.loseInfoModalOptions);
  }

  /**
   * Helper method to indicate if the current section is dirty, i.e the user has typed in data that hasn't been saved
   * yet.
   *
   * @param aeroFormComponent
   */
  public sectionIsDirty(aeroFormComponent: AeroFormComponent): boolean {
    return (aeroFormComponent) ? aeroFormComponent.form.dirty : false;
  }

  /**
   * Helper method to determine if the form has any form data, i.e the user has entered anything into any of the
   * form fields.
   *
   * @param aeroFormComponent
   * @private
   */
  public hasFormData(aeroFormComponent: AeroFormComponent): boolean {
    let hasFormData: boolean = false;
    for (const [key, value] of Object.entries(aeroFormComponent.form.value)) {
      if (value) {
        hasFormData = true;
      }
    }
    return hasFormData;
  }

  /**
   * Method to update the portfolio content map for the specific resume section if the user modified the portfolio
   * content.
   *
   * @param subSectionType
   * @param subSectionIndex
   */
  public updatePortfolioContentModifiedMap(subSectionType : InstanceType<any>, subSectionIndex ?: number): void {
    let formControl : UntypedFormControl;
    if (typeof (subSectionIndex) === 'undefined') {
      const formControls : any = subSectionType.aeroFormComponent.form.controls;
      formControl = formControls[Object.keys(formControls)[0]];
    } else {
      const aeroFormComponent : AeroFormComponent = subSectionType.aeroFormComponentList.toArray()[subSectionIndex];
      const formControls : any = aeroFormComponent.form.controls;
      formControl = formControls[Object.keys(formControls)[0]];
    }
    formControl?.markAsDirty();
    this.isPortfolioContentModified = true;
  }

  /**
   * Helper method to reset the state of the portfolio content modified map. For instance after user hits save, cancel,
   * etc...
   */
  resetPortfolioContentModifiedMap(): void {
    this.isPortfolioContentModified = false;
  }

  /**
   * Method that handles if the user closes the page, or navigates to another website.
   */
  private static registerBeforeUnloadEvent(): void {
    window.addEventListener('beforeunload', SectionDataAlertService.handleBeforeUnloadEvent);
  }

  /**
   * Helper method to unregister the beforeunload event on the window object.
   */
  unregisterBeforeUnloadEvent(): void {
    window.removeEventListener('beforeunload', SectionDataAlertService.handleBeforeUnloadEvent);
  }

  /**
   * Helper method that handles the beforeunload event.
   *
   * @param event
   */
  private static handleBeforeUnloadEvent(event: any): void {
    // Needed for Chrome:
    event.returnValue = '';
    event.preventDefault();
  }

  /**
   * Helper method to update the save button state when portfolio content is added/removed - Enabled/Disabled
   * @param subSectionType
   * @param subSectionIndex
   */
  public handleSaveButtonForAddRemovePortfolioContent(subSectionType : InstanceType< any >, subSectionIndex ?: number) {
    let isFormValid = false;
    if (typeof (subSectionIndex) === 'undefined') {
      if (subSectionType.customSection) {
        isFormValid = !subSectionType.aeroFormComponent.form.valid || subSectionType.charLimitReached;
      }

      if (subSectionType.aboutMeSection) {
        isFormValid = subSectionType.charLimitReached;
      }
    } else {
      subSectionType.aeroFormComponent = subSectionType.aeroFormComponentList.toArray()[subSectionIndex];
      if (subSectionType.experienceSection) {
        isFormValid = !subSectionType.aeroFormComponent.form.valid || subSectionType.charLimitReached;
      }
    }
    if (isFormValid) {
      subSectionType.aeroFormComponent.markAeroFormAsPristine();
    }
    subSectionType.aeroFormComponent.updateSaveButtonState(isFormValid);
  }

  /**
   * Helper method to update the save button state when subsection content is updated.
   * @param aeroFormComponent
   * @param charLimitReached
   */
  public handleSaveOnContentUpdate(aeroFormComponent: AeroFormComponent, charLimitReached: boolean = false) {
    if (charLimitReached) {
      aeroFormComponent.markAeroFormAsPristine();
      aeroFormComponent.updateSaveButtonState();
    } else {
      aeroFormComponent.updateSaveButtonState(!aeroFormComponent.form.form.valid);
    }
    aeroFormComponent.form.form.markAsDirty();
  }
}
